// Each page's code, loaded the first time it's needed (Phase 11): the router's `lazy` routes use these, so the first
// screen downloads only its own page, not the flow diagram or the staff console. The sidebar calls the same loaders
// when the pointer rests on a link, so a section's code is usually there by the click. A loader runs its import once;
// later calls reuse it.
//
//   { path: 'home', lazy: pages.home }      → { Component: HomePage }

import type { ComponentType } from 'react'

type Lazy = () => Promise<{ Component: ComponentType }>

/** One page module's export, as a route's lazy result. */
const _page =
  <Module>(load: () => Promise<Module>, pick: (module: Module) => ComponentType): Lazy =>
  async () => ({ Component: pick(await load()) })

export const pages = {
  // Signed out
  login: _page(
    () => import('@/routes/auth/LoginPage'),
    (m) => m.LoginPage,
  ),
  signup: _page(
    () => import('@/routes/auth/SignupPage'),
    (m) => m.SignupPage,
  ),
  checkEmail: _page(
    () => import('@/routes/auth/CheckEmailPage'),
    (m) => m.CheckEmailPage,
  ),
  verifyEmail: _page(
    () => import('@/routes/auth/VerifyEmailPage'),
    (m) => m.VerifyEmailPage,
  ),
  authorize: _page(
    () => import('@/routes/oauth/AuthorizePage'),
    (m) => m.AuthorizePage,
  ),
  forgotPassword: _page(
    () => import('@/routes/auth/ForgotPasswordPage'),
    (m) => m.ForgotPasswordPage,
  ),
  resetPassword: _page(
    () => import('@/routes/auth/ResetPasswordPage'),
    (m) => m.ResetPasswordPage,
  ),
  invite: _page(
    () => import('@/routes/auth/InvitePage'),
    (m) => m.InvitePage,
  ),
  unsubscribe: _page(
    () => import('@/routes/auth/UnsubscribePage'),
    (m) => m.UnsubscribePage,
  ),
  join: _page(
    () => import('@/routes/auth/JoinPage'),
    (m) => m.JoinPage,
  ),
  onboarding: _page(
    () => import('@/routes/onboarding/OnboardingPage'),
    (m) => m.OnboardingPage,
  ),
  newOrg: _page(
    () => import('@/routes/orgs/NewOrgPage'),
    (m) => m.NewOrgPage,
  ),
  // An org's sections
  home: _page(
    () => import('@/routes/app/home/HomePage'),
    (m) => m.HomePage,
  ),
  calls: _page(
    () => import('@/routes/app/calls/CallsPage'),
    (m) => m.CallsPage,
  ),
  contacts: _page(
    () => import('@/routes/app/contacts/ContactsPage'),
    (m) => m.ContactsPage,
  ),
  flows: _page(
    () => import('@/routes/app/flows/FlowsPage'),
    (m) => m.FlowsPage,
  ),
  libraryFlow: _page(
    () => import('@/routes/app/flows/LibraryFlowPage'),
    (m) => m.LibraryFlowPage,
  ),
  flowEditor: _page(
    () => import('@/routes/app/flows/FlowEditorPage'),
    (m) => m.FlowEditorPage,
  ),
  results: _page(
    () => import('@/routes/app/results/ResultsPage'),
    (m) => m.ResultsPage,
  ),
  analytics: _page(
    () => import('@/routes/app/analytics/AnalyticsPage'),
    (m) => m.AnalyticsPage,
  ),
  integrations: _page(
    () => import('@/routes/app/integrations/IntegrationsPage'),
    (m) => m.IntegrationsPage,
  ),
  orders: _page(
    () => import('@/routes/app/orders/OrdersPage'),
    (m) => m.OrdersPage,
  ),
  stores: _page(
    () => import('@/routes/app/stores/StoresPage'),
    (m) => m.StoresPage,
  ),
  store: _page(
    () => import('@/routes/app/stores/StorePage'),
    (m) => m.StorePage,
  ),
  invoice: _page(
    () => import('@/routes/app/billing/InvoicePage'),
    (m) => m.InvoicePage,
  ),
  business: _page(
    () => import('@/routes/app/business/BusinessPage'),
    (m) => m.BusinessPage,
  ),
  developers: _page(
    () => import('@/routes/app/developers/DevelopersPage'),
    (m) => m.DevelopersPage,
  ),
  accountLayout: _page(
    () => import('@/routes/account/AccountLayout'),
    (m) => m.AccountLayout,
  ),
  myAlerts: _page(
    () => import('@/routes/account/MyAlertsPage'),
    (m) => m.MyAlertsPage,
  ),
  account: _page(
    () => import('@/routes/account/AccountPage'),
    (m) => m.AccountPage,
  ),
  // Settings
  settings: _page(
    () => import('@/routes/settings/SettingsLayout'),
    (m) => m.SettingsLayout,
  ),
  generalSettings: _page(
    () => import('@/routes/settings/GeneralSettings'),
    (m) => m.GeneralSettings,
  ),
  voicesSettings: _page(
    () => import('@/routes/settings/VoicesSettings'),
    (m) => m.VoicesSettings,
  ),
  sopSettings: _page(
    () => import('@/routes/settings/SopSettings'),
    (m) => m.SopSettings,
  ),
  phoneNumbersSettings: _page(
    () => import('@/routes/settings/PhoneNumbersSettings'),
    (m) => m.PhoneNumbersSettings,
  ),
  membersSettings: _page(
    () => import('@/routes/settings/MembersSettings'),
    (m) => m.MembersSettings,
  ),
  rolesSettings: _page(
    () => import('@/routes/settings/RolesSettings'),
    (m) => m.RolesSettings,
  ),
  auditSettings: _page(
    () => import('@/routes/settings/AuditSettings'),
    (m) => m.AuditSettings,
  ),
  connectedAppsSettings: _page(
    () => import('@/routes/settings/ConnectedAppsSettings'),
    (m) => m.ConnectedAppsSettings,
  ),
  alertsSettings: _page(
    () => import('@/routes/settings/AlertsSettings'),
    (m) => m.AlertsSettings,
  ),
  billingSettings: _page(
    () => import('@/routes/settings/BillingSettings'),
    (m) => m.BillingSettings,
  ),
  // The staff console
  admin: _page(
    () => import('@/routes/admin/AdminLayout'),
    (m) => m.AdminLayout,
  ),
  adminOrgs: _page(
    () => import('@/routes/admin/AdminOrgsPage'),
    (m) => m.AdminOrgsPage,
  ),
  adminOrg: _page(
    () => import('@/routes/admin/AdminOrgPage'),
    (m) => m.AdminOrgPage,
  ),
  adminCalls: _page(
    () => import('@/routes/admin/AdminCallsPage'),
    (m) => m.AdminCallsPage,
  ),
  adminCall: _page(
    () => import('@/routes/admin/AdminCallPage'),
    (m) => m.AdminCallPage,
  ),
  adminNumbers: _page(
    () => import('@/routes/admin/AdminNumbersPage'),
    (m) => m.AdminNumbersPage,
  ),
  adminWaitlist: _page(
    () => import('@/routes/admin/AdminWaitlistPage'),
    (m) => m.AdminWaitlistPage,
  ),
  adminLeads: _page(
    () => import('@/routes/admin/AdminLeadsPage'),
    (m) => m.AdminLeadsPage,
  ),
  adminInvites: _page(
    () => import('@/routes/admin/AdminInvitesPage'),
    (m) => m.AdminInvitesPage,
  ),
  adminStores: _page(
    () => import('@/routes/admin/AdminStoresPage'),
    (m) => m.AdminStoresPage,
  ),
  adminAlerts: _page(
    () => import('@/routes/admin/AdminAlertsPage'),
    (m) => m.AdminAlertsPage,
  ),
  adminApproval: _page(
    () => import('@/routes/admin/AdminApprovalPage'),
    (m) => m.AdminApprovalPage,
  ),
  adminApprovals: _page(
    () => import('@/routes/admin/AdminApprovalsPage'),
    (m) => m.AdminApprovalsPage,
  ),
  adminUsers: _page(
    () => import('@/routes/admin/AdminUsersPage'),
    (m) => m.AdminUsersPage,
  ),
  adminPlatforms: _page(
    () => import('@/routes/admin/AdminPlatformsPage'),
    (m) => m.AdminPlatformsPage,
  ),
  adminApps: _page(
    () => import('@/routes/admin/AdminAppsPage'),
    (m) => m.AdminAppsPage,
  ),
} satisfies Record<string, Lazy>

/** The page the sidebar's section opens (lib/navigation.ts paths), to start loading on hover. */
export const SECTION_PAGE: Record<string, Lazy> = {
  home: pages.home,
  calls: pages.calls,
  contacts: pages.contacts,
  orders: pages.orders,
  flows: pages.flows,
  results: pages.results,
  analytics: pages.analytics,
  stores: pages.stores,
  integrations: pages.integrations,
  developers: pages.developers,
  settings: pages.settings,
}

/** Loads a section's code ahead of a click (on hover or focus); safe to call often. */
export const prefetchSection = async (section: string): Promise<void> => {
  try {
    await SECTION_PAGE[section]?.()
  } catch {
    // Offline or a new release: the click loads it (and reports a failure) as usual.
  }
}
